import { useEffect, useState } from 'react'

/**
 * The page's single ticking clock.
 *
 * Late is derived — a pure function of `scheduledFor`, the stage and the
 * current time — so nothing is stored or pushed; the only problem is what
 * makes React re-render as time passes. One shared clock per page answers
 * that: every consumer re-renders together, and the board can never show two
 * different opinions of "now".
 *
 * The interval covers the board sitting open; the focus / visibilitychange
 * listeners cover what the interval handles badly — a tab left in the
 * background or a tablet that slept, where the next tick could be seconds
 * away but the user is looking *now*.
 */
export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const update = () => setNow(new Date())

    const id = window.setInterval(update, intervalMs)
    window.addEventListener('focus', update)
    document.addEventListener('visibilitychange', update)

    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', update)
      document.removeEventListener('visibilitychange', update)
    }
  }, [intervalMs])

  return now
}
